import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import SpaceBackground from '../components/layout/SpaceBackground';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [clientStatusFilter, setClientStatusFilter] = useState('all'); // 'all', 'active', 'former', 'prospective'
  const [currentPage, setCurrentPage] = useState(1);
  const [totalContacts, setTotalContacts] = useState(0);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(null);
  const pageSize = 50;
  const navigate = useNavigate();
  const debounceTimer = useRef(null);
  const searchInputRef = useRef(null);

  // Debounce search term
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 300);
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [searchTerm]);

  const isInitialMount = useRef(true);

  // Initial load
  useEffect(() => {
    fetchContacts(true);
    fetchStats();
  }, []);

  // Search/filter changes - don't show loading spinner to avoid UI flicker
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    fetchContacts(false);
  }, [currentPage, debouncedSearchTerm, filterType, clientStatusFilter]);

  const fetchContacts = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      const token = localStorage.getItem('token');

      const params = {
        page: currentPage,
        page_size: pageSize
      };

      if (debouncedSearchTerm) params.search = debouncedSearchTerm;
      if (filterType !== 'all') params.contact_type = filterType;

      // Handle client status filter
      if (clientStatusFilter !== 'all') {
        params.is_client = true;
        params.client_status = clientStatusFilter;
      }

      const response = await axios.get(`${API_URL}/contacts/`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });

      setContacts(response.data.contacts || []);
      setTotalContacts(response.data.total || 0);
      setError(null);
    } catch (err) {
      console.error('Error fetching contacts:', err);
      setError('Failed to load contacts');
      setContacts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/contacts/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    // currentPage is reset in the debounce effect
  };

  const handleFilterChange = (type) => {
    setFilterType(type);
    setCurrentPage(1);
  };

  const handleClientStatusChange = (status) => {
    setClientStatusFilter(status);
    setCurrentPage(1);
  };

  const handleSendBulkEmail = async () => {
    if (!emailSubject.trim() || !emailBody.trim()) {
      alert('Please enter both subject and message');
      return;
    }

    try {
      setSendingEmail(true);
      const token = localStorage.getItem('token');

      const response = await axios.post(`${API_URL}/mail/compose/send-mass-email`, {
        subject: emailSubject,
        body: emailBody,
        recipient_filter: 'client_status',
        client_status: clientStatusFilter,
        from_firm: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setEmailSuccess(`Successfully queued ${response.data.queued_count} emails for sending`);
      setEmailSubject('');
      setEmailBody('');
      setTimeout(() => {
        setShowEmailModal(false);
        setEmailSuccess(null);
      }, 3000);
    } catch (err) {
      console.error('Error sending bulk email:', err);
      alert('Failed to send emails: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSendingEmail(false);
    }
  };

  const getClientStatusLabel = (status) => {
    switch (status) {
      case 'active': return 'Active Client';
      case 'former': return 'Former Client';
      case 'prospective': return 'Prospective Client';
      default: return status;
    }
  };

  const getClientStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'former': return 'bg-gray-100 text-gray-800';
      case 'prospective': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  const totalPages = Math.ceil(totalContacts / pageSize);

  if (loading && contacts.length === 0) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl relative">
      <SpaceBackground />
      <div className="relative z-10">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-virgo-teal-100">Contacts</h1>
          <p className="text-virgo-teal-300 mt-1">Manage all your legal contacts</p>
        </div>
        <div className="flex gap-3">
          {clientStatusFilter !== 'all' && (
            <button
              onClick={() => setShowEmailModal(true)}
              className="bg-virgo-navy-600 text-white px-4 py-2 rounded-lg hover:bg-virgo-navy-500 transition flex items-center border border-virgo-teal-600"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Email {getClientStatusLabel(clientStatusFilter)}s
            </button>
          )}
          <button
            onClick={() => navigate('/contacts/new')}
            className="bg-virgo-teal-600 text-white px-6 py-2 rounded-lg hover:bg-virgo-teal-700 transition flex items-center"
          >
            <span className="mr-2">+</span>
            New Contact
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
          <div className="bg-virgo-navy-700 p-4 rounded-lg shadow">
            <div className="text-virgo-teal-300 text-sm">Total Contacts</div>
            <div className="text-2xl font-bold text-virgo-teal-100">{stats.total_contacts || 0}</div>
          </div>
          <div
            className={`bg-virgo-navy-700 p-4 rounded-lg shadow cursor-pointer transition hover:ring-2 hover:ring-virgo-teal-500 ${clientStatusFilter === 'all' && filterType === 'all' ? 'ring-2 ring-virgo-teal-500' : ''}`}
            onClick={() => { handleClientStatusChange('all'); handleFilterChange('all'); }}
          >
            <div className="text-virgo-teal-300 text-sm">All Clients</div>
            <div className="text-2xl font-bold text-virgo-teal-400">{stats.total_clients || 0}</div>
          </div>
          <div
            className={`bg-virgo-navy-700 p-4 rounded-lg shadow cursor-pointer transition hover:ring-2 hover:ring-green-500 ${clientStatusFilter === 'active' ? 'ring-2 ring-green-500' : ''}`}
            onClick={() => handleClientStatusChange('active')}
          >
            <div className="text-virgo-teal-300 text-sm">Active Clients</div>
            <div className="text-2xl font-bold text-green-500">{stats.active_clients || 0}</div>
          </div>
          <div
            className={`bg-virgo-navy-700 p-4 rounded-lg shadow cursor-pointer transition hover:ring-2 hover:ring-yellow-500 ${clientStatusFilter === 'prospective' ? 'ring-2 ring-yellow-500' : ''}`}
            onClick={() => handleClientStatusChange('prospective')}
          >
            <div className="text-virgo-teal-300 text-sm">Prospective</div>
            <div className="text-2xl font-bold text-yellow-500">{stats.prospective_clients || 0}</div>
          </div>
          <div
            className={`bg-virgo-navy-700 p-4 rounded-lg shadow cursor-pointer transition hover:ring-2 hover:ring-gray-400 ${clientStatusFilter === 'former' ? 'ring-2 ring-gray-400' : ''}`}
            onClick={() => handleClientStatusChange('former')}
          >
            <div className="text-virgo-teal-300 text-sm">Former Clients</div>
            <div className="text-2xl font-bold text-gray-400">{stats.former_clients || 0}</div>
          </div>
          <div className="bg-virgo-navy-700 p-4 rounded-lg shadow">
            <div className="text-virgo-teal-300 text-sm">Attorneys</div>
            <div className="text-2xl font-bold text-blue-400">{stats.total_attorneys || 0}</div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-virgo-navy-700 rounded-lg shadow mb-6 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search contacts by name, email, or company..."
              value={searchTerm}
              onChange={handleSearch}
              autoComplete="off"
              className="w-full px-4 py-2 border border-virgo-teal-700 rounded-lg focus:ring-2 focus:ring-virgo-teal-500 focus:border-transparent bg-virgo-navy-800 text-white"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => { handleFilterChange('all'); handleClientStatusChange('all'); }}
              className={`px-4 py-2 rounded-lg ${
                filterType === 'all' && clientStatusFilter === 'all'
                  ? 'bg-virgo-teal-600 text-white'
                  : 'bg-virgo-navy-800 text-virgo-teal-200 hover:bg-virgo-navy-600'
              }`}
            >
              All
            </button>
            <select
              value={clientStatusFilter}
              onChange={(e) => handleClientStatusChange(e.target.value)}
              className="px-4 py-2 rounded-lg bg-virgo-navy-800 text-virgo-teal-200 border border-virgo-teal-700"
            >
              <option value="all">All Clients</option>
              <option value="active">Active Clients</option>
              <option value="prospective">Prospective Clients</option>
              <option value="former">Former Clients</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* Contacts Table */}
      <div className="bg-virgo-navy-700 rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-virgo-navy-600">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                Company
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                Phone
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="bg-virgo-navy-700 divide-y divide-gray-700">
            {contacts && contacts.length > 0 ? (
              contacts.map((contact) => (
                <tr
                  key={contact.id}
                  onClick={() => navigate(`/contacts/${contact.id}`)}
                  className="hover:bg-virgo-navy-600 cursor-pointer transition"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-virgo-navy-500 rounded-full flex items-center justify-center">
                        <span className="text-virgo-teal-400 font-semibold">
                          {contact.first_name?.[0] || contact.contact_name?.[0] || '?'}{contact.last_name?.[0] || ''}
                        </span>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-white">
                          {contact.contact_name || 'Unknown'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-virgo-navy-800 text-virgo-teal-100">
                      {contact.entity || 'N/A'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-virgo-teal-400">
                    {contact.company || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-virgo-teal-400">
                    {contact.primary_email_address || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-virgo-teal-400">
                    {contact.primary_phone_number || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {contact.client_status ? (
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getClientStatusColor(contact.client_status)}`}>
                        {getClientStatusLabel(contact.client_status)}
                      </span>
                    ) : contact.is_client ? (
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                        Client
                      </span>
                    ) : null}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="px-6 py-12 text-center">
                  <p className="text-virgo-teal-400 text-lg">No contacts found</p>
                  <p className="text-virgo-teal-500 text-sm mt-2">Try adjusting your search or filters</p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center mt-6 gap-2">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 bg-virgo-navy-700 border border-virgo-teal-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-virgo-navy-600 text-white"
          >
            Previous
          </button>
          <span className="px-4 py-2 text-virgo-teal-200">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-4 py-2 bg-virgo-navy-700 border border-virgo-teal-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-virgo-navy-600 text-white"
          >
            Next
          </button>
        </div>
      )}

      {/* Bulk Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-virgo-navy-700 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-white">
                  Email All {getClientStatusLabel(clientStatusFilter)}s
                </h2>
                <button
                  onClick={() => setShowEmailModal(false)}
                  className="text-virgo-teal-400 hover:text-white"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {emailSuccess ? (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
                  {emailSuccess}
                </div>
              ) : (
                <>
                  <div className="mb-4">
                    <p className="text-virgo-teal-300 text-sm mb-4">
                      This will send an email to all {clientStatusFilter} clients with email addresses.
                      The email will be sent from the firm email.
                    </p>
                  </div>

                  <div className="mb-4">
                    <label className="block text-virgo-teal-200 text-sm font-medium mb-2">
                      Subject
                    </label>
                    <input
                      type="text"
                      value={emailSubject}
                      onChange={(e) => setEmailSubject(e.target.value)}
                      placeholder="Enter email subject..."
                      className="w-full px-4 py-2 border border-virgo-teal-700 rounded-lg bg-virgo-navy-800 text-white focus:ring-2 focus:ring-virgo-teal-500"
                    />
                  </div>

                  <div className="mb-6">
                    <label className="block text-virgo-teal-200 text-sm font-medium mb-2">
                      Message
                    </label>
                    <textarea
                      value={emailBody}
                      onChange={(e) => setEmailBody(e.target.value)}
                      placeholder="Enter your message..."
                      rows={8}
                      className="w-full px-4 py-2 border border-virgo-teal-700 rounded-lg bg-virgo-navy-800 text-white focus:ring-2 focus:ring-virgo-teal-500"
                    />
                    <p className="text-virgo-teal-400 text-xs mt-2">
                      The message will be personalized with "Dear [Client Name]," at the beginning.
                    </p>
                  </div>

                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => setShowEmailModal(false)}
                      className="px-4 py-2 bg-virgo-navy-600 text-white rounded-lg hover:bg-virgo-navy-500"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSendBulkEmail}
                      disabled={sendingEmail || !emailSubject.trim() || !emailBody.trim()}
                      className="px-6 py-2 bg-virgo-teal-600 text-white rounded-lg hover:bg-virgo-teal-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                      {sendingEmail ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Sending...
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                          </svg>
                          Send to All
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

export default Contacts;
